import * as React from "react";
import { Loader } from "semantic-ui-react";
import { BasicFormProps, Media, Prompt, PromptType } from "@/interfaces";
import { Rules, useSocialCoachSession } from "@/utils";
import {
  PromptMedia,
  CaptionSettings,
  MediaHelpPopover,
  CaptionSection,
  PromptCategories,
  PromptTypeSelector,
} from "./components";
import { Alert, Field, Input } from "@/components/ui";
import { cn } from "@/lib/utils";
import { AlertCircle } from "lucide-react";

interface OwnProps extends BasicFormProps {
  prompt?: Prompt;
  onUploading: (uploading: boolean) => void;
  onNewMedia: (newMedia: Media[]) => void;

  /**
   * Handle datePickers changes
   */
  handleSetFieldValue?: (field: string, value: any) => void;
  /**
   * Handle cancel action
   */
  handleCancel?: () => void;

  modeEditable: boolean;
  /**
   * To Indicate if the form is disabled
   */
  disabled: boolean;
}

type Props = OwnProps;

/**
 * Represents a Prompt content.
 */
const PromptClass: React.FC<Props> = ({
  prompt,
  handleSetFieldValue,
  onUploading,
  onNewMedia,
  messages,
  isSubmitting = false,
  values,
  disabled,
}) => {
  const { coachId } = useSocialCoachSession();

  React.useEffect(() => {
    return () => {
      window.removeEventListener("loadedmetadata", () => {
        // Nothing
      });
    };
  }, []);

  return (
    <div className={cn(disabled ? "opacity-50 pointer-events-none" : "")}>
      {isSubmitting && <Loader active size="large" />}

      <div key={"promptFormDiv"} className={"flex flex-col gap-6"}>
        {messages?.error?.map((m, i) => (
          <Alert variant="destructive" key={`prompt-error-${i}`}>
            <AlertCircle />
            <Alert.Title>{m}</Alert.Title>
          </Alert>
        ))}

        <PromptTypeSelector
          values={values}
          disabled={disabled || isSubmitting}
          handleSetFieldValue={handleSetFieldValue}
        />

        <Field>
          <Field.Label>Prompt Title</Field.Label>
          <Input
            key={"groupTitleInput"}
            data-elm-id={"promptInputTitle"}
            id={"title"}
            type={"text"}
            name={"title"}
            placeholder={"Enter a title for your prompt..."}
            value={values.title}
            disabled={disabled}
            maxLength={Rules.MAX_TITLE}
            onChange={data => {
              handleSetFieldValue!("title", data.currentTarget.value);
            }}
            className="bg-white"
          />
        </Field>

        <PromptCategories
          values={values}
          disabled={disabled || isSubmitting}
          handleSetFieldValue={handleSetFieldValue}
        />

        {/* "Image/Video for Post" section */}
        <Field>
          <div className="flex flex-row gap-2 items-center">
            <Field.Label>{values.type === PromptType.REELS ? "Video" : "Prompt Media"}</Field.Label>
            <MediaHelpPopover type={values.type} />
          </div>

          <PromptMedia coachId={coachId!} values={values} onUploading={onUploading} onNewMedia={onNewMedia} />
        </Field>

        <CaptionSection values={values} handleSetFieldValue={handleSetFieldValue} disabled={disabled} />

        <CaptionSettings
          prompt={prompt}
          values={values}
          handleSetFieldValue={handleSetFieldValue}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export const PromptComponent = PromptClass;
